import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Storefront } from '../types';
import { transformStepsToAPI } from './useFunnelData';

interface UseJsonEditorProps {
  funnel: Storefront;
  setFunnel: (funnel: Storefront) => void;
  viewMode: 'visual' | 'flow' | 'json';
  setViewMode: (mode: 'visual' | 'flow' | 'json') => void;
}

/**
 * Custom hook for JSON editor functionality
 * Handles JSON serialization, parsing, and transformation
 */
export function useJsonEditor({ funnel, setFunnel, viewMode, setViewMode }: UseJsonEditorProps) {
  const [jsonValue, setJsonValue] = useState<string>('');

  // Update JSON value when funnel changes or switching to JSON mode
  useEffect(() => {
    if (viewMode === 'json' && funnel) {
      const transformedSteps = transformStepsToAPI(funnel.steps);
      const funnelConfig = {
        id: funnel.slug,
        name: funnel.name,
        description: '',
        version: '1.0.0',
        steps: transformedSteps,
        nodes: funnel.nodes,
        edges: funnel.edges,
        metadata: {},
      };
      setJsonValue(JSON.stringify(funnelConfig, null, 2));
    }
  }, [viewMode, funnel]);

  // Handle applying JSON changes
  const handleApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonValue);

      // Validate basic structure
      if (!parsed.steps || !Array.isArray(parsed.steps)) {
        toast.error('Invalid JSON: "steps" array is required');
        return;
      }

      // Transform steps to add position and missing fields if needed (for visual editor compatibility)
      const transformedSteps = parsed.steps
        .filter((s: any) => s)
        .map((step: any, index: number) => {
          // Calculate column position
          const col =
            step.position?.col ??
            (typeof step.row === 'number' ? step.row : step.index ? step.index - 1 : index);

          // Ensure step has all required fields for visual editor
          const transformedStep: any = {
            id: step.id || `step_${Date.now()}_${index}`,
            label: step.label || step.name || `Page ${col + 1}`,
            type: step.type || 'landing',
            position: {
              col: col,
              row: step.position?.row ?? 0,
            },
            config: {
              url: step.config?.url || '',
              pagePath: step.config?.path || step.config?.pagePath || '',
              mountPointId: step.config?.mountPointId || undefined,
              instanceId: step.config?.instanceId || undefined,
              metadata: step.config?.metadata || {},
            },
            conditions: step.conditions || {},
            connections: step.connections || [],
          };

          // Only add optional fields if they exist
          if (step.variants) transformedStep.variants = step.variants;
          if (step.splitType) transformedStep.splitType = step.splitType;
          if (step.alternativeType) transformedStep.alternativeType = step.alternativeType;
          if (step.metrics) transformedStep.metrics = step.metrics;

          // Transfer conditions.entry to root-level isDefault for backend compatibility
          if (step.conditions?.entry || step.isDefault) {
            transformedStep.isDefault = true;
          }

          return transformedStep;
        });

      // Update funnel with parsed JSON
      setFunnel({
        ...funnel,
        name: parsed.name || funnel.name,
        slug: parsed.id || funnel.slug,
        steps: transformedSteps,
        nodes: parsed.nodes,
        edges: parsed.edges,
      });

      toast.success(`JSON applied successfully - ${transformedSteps.length} steps loaded`);
      setViewMode('visual');
    } catch (error: any) {
      toast.error(`Invalid JSON: ${error.message}`);
    }
  };

  // Handle copying JSON to clipboard
  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonValue);
      toast.success('JSON copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy JSON');
    }
  };

  return {
    jsonValue,
    setJsonValue,
    handleApplyJson,
    handleCopyJson,
  };
}

